import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createHashHistory } from 'history';
import { Prompt } from 'react-router-dom';
import { Alert } from 'reactstrap';
import entityComposition from '../common/classes/entityComposition';
import PDF from '../common/pdf/pdf';
import FormAnnotationEditorSidebar from './formAnnotationEditorSidebar';
import { DEFAULT_COLOR } from '../../reducers/annotationSelectionReducer';
import routeHelper from '../common/classes/routeHelper';
import store from '../../reducers/storeRegistration';
import annTools from '../common/pdf/annotations/commonAnnotationTools';
import { evaluateAnnotationHandlerCreate } from '../../actions/formActionCreators';
const hashHistory = createHashHistory();

/**
 * Evaluate annotationHandler.create to get the color for an annotation before drawing starts
 * This is used to get the correct color for temporary lines/inks during drawing
 * @param {string} baseType - Base type ('line' or 'ink')
 * @param {string} formTypeIdentifier - Form type identifier
 * @param {Object} annotationHandler - Source behaviors to evaluate
 * @param {Object} parent - Parent content and path
 * @param {string} defaultColor - Default color string
 * @returns {Promise<string|null>} Color string or null if no handler or error
 */
const evaluateAnnotationHandlerColor = async (baseType, formTypeIdentifier, annotationHandler, parent, defaultColor) => {
  var _evaluatedContent$ren;
  // Create a minimal proposedContent for initial evaluation
  // We don't have points yet, so we create a basic render object
  const proposedContent = {
    render: {
      renderType: baseType === 'line' ? 'Line' : 'Ink',
      color: annTools.toRGBAObject(defaultColor)
    }
  };
  const evaluatedContent = await evaluateAnnotationHandlerCreate(annotationHandler, parent, proposedContent, formTypeIdentifier);
  const newColor = evaluatedContent === null || evaluatedContent === void 0 || (_evaluatedContent$ren = evaluatedContent.render) === null || _evaluatedContent$ren === void 0 ? void 0 : _evaluatedContent$ren.color;
  if (!newColor || newColor.red === undefined || newColor.green === undefined || newColor.blue === undefined || typeof newColor.red !== 'number' || typeof newColor.green !== 'number' || typeof newColor.blue !== 'number') {
    return null;
  }
  return annTools.toRGBAString(newColor);
};
const FormAnnotationEditor = ({
  annotationSelection: {
    defaultColor,
    editMode,
    selectableType
  },
  annotation: {
    active,
    editorState: {
      annotationHandler,
      parent,
      remotePath: {
        _cache: cachePath,
        _download: annotationDownloadPDFPath
      },
      renderReadonly,
      title,
      filters,
      toolbar
    },
    isLoading
  },
  form: {
    pendingChanges,
    downloadPDFPath
  },
  annotationActions: {
    loadPersistedState,
    loadAnnotations
  },
  annotationSelectionActions: {
    clearSelection,
    select,
    setAnnotationContext,
    setDefaultColor,
    setSelectableType
  },
  formActions: {
    serverSave,
    undoChanges,
    createFormAnnotation,
    serverRenderAnnotatedForm,
    clearDownloadPDFPath
  },
  notificationActions
}) => {
  const [maxHeight, setMaxHeight] = useState(1200);
  const [leftWidth, setLeftWidth] = useState(75);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  /**
   * Calculate & Update state of new dimensions
   */
  const updateDimensions = () => {
    // footer is 30 + 20
    // header is 59 + 30
    const verticalMargin = 139;
    if (global.window.innerHeight < 2 * verticalMargin) {
      setMaxHeight(verticalMargin);
    } else {
      setMaxHeight(global.window.innerHeight - verticalMargin);
    }
  };
  const handleMouseDown = e => {
    setIsDragging(true);
    e.preventDefault();
  };
  const handleMouseMove = useCallback(e => {
    if (!isDragging) return;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const newLeftWidth = (e.clientX - containerRect.left) / containerRect.width * 100;

    // Limit the width between 20% and 80%
    const clampedWidth = Math.min(Math.max(newLeftWidth, 20), 80);

    // Only update if the change is significant enough (0.5% threshold)
    // This prevents excessive rerenders while still being responsive
    if (Math.abs(clampedWidth - leftWidth) >= 0.5) {
      setLeftWidth(clampedWidth);
    }
  }, [isDragging, leftWidth]);
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  useEffect(() => {
    updateDimensions();
    global.window.addEventListener('resize', updateDimensions);
    return () => {
      global.window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  useEffect(() => {
    if (isDragging) {
      global.window.addEventListener('mousemove', handleMouseMove);
      global.window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      global.window.removeEventListener('mousemove', handleMouseMove);
      global.window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Load persisted state when component mounts
  useEffect(() => {
    loadPersistedState();
  }, [loadPersistedState]);

  // Load annotations when editor state changes
  useEffect(() => {
    // if (cachePath) {
    //   loadAnnotations();
    // }
    loadAnnotations();
  }, [loadAnnotations]);

  // Helper to build composite identifier
  const getAnnotationIdentifier = (renderType, formType) => {
    const render = renderType || '';
    // Use formType.identifier if available
    const form = formType && formType.identifier ? `__${formType.identifier}` : '';
    return `${render}${form}`;
  };
  const handleClearSelection = useCallback(() => {
    // NOTE: Using store.getState() to avoid React stale closure issues.
    // Callbacks can be created with stale props even after component re-renders.
    // Reading from store at execution time ensures we get current state.
    const currentState = store.getState();
    const currentPendingChanges = currentState.form.pendingChanges;

    // Check for pending changes before clearing selection
    if (currentPendingChanges) {
      return false; // Indicate that clear was blocked
    }
    clearSelection();
    return true; // Indicate that clear was successful
  }, [clearSelection]);
  const handleSave = useCallback(() => {
    // NOTE: Using store.getState() to avoid React stale closure issues.
    // Callbacks can be created with stale props even after component re-renders.
    // Reading from store at execution time ensures we get current state.
    const currentState = store.getState();
    const currentPendingChanges = currentState.form.pendingChanges;
    if (currentPendingChanges) {
      serverSave();
    }
  }, [serverSave]);

  // Handle transitions (e.g., navigation away, annotation creation) safely
  const handleTransition = useCallback((proceed = () => {}) => {
    // NOTE: Using store.getState() to avoid React stale closure issues.
    // Callbacks can be created with stale props even after component re-renders.
    // Reading from store at execution time ensures we get current state.
    const currentState = store.getState();
    const currentPendingChanges = currentState.form.pendingChanges;
    const currentFormLoading = currentState.form.loading;

    // Block transition if loading (but only show notification if there are changes to protect)
    if (isLoading || currentFormLoading) {
      // Only show loading notification if there are pending changes or active annotation
      // This prevents showing loading notification when just selecting tools
      if (currentPendingChanges || active && active.id) {
        const notificationID = 'transition_while_loading';
        notificationActions.dismiss(notificationID);
        notificationActions.info({
          message: 'Please wait for data to finish loading before transitioning away.',
          id: notificationID
        }, {
          autoClose: 2000 // ensures only one notification at a time
        });
        return false; // tells Prompt to block navigation
      }
    }
    if (currentPendingChanges) {
      const noHandler = () => {
        // First undo changes to clear pending changes
        undoChanges();
        // Then clear active selection
        handleClearSelection();
        proceed();
      };
      hashHistory.replace('/annotations'); // block navigation
      routeHelper.displayModal(handleSave, noHandler);
      return false; // tells Prompt to block navigation
    }

    // clear active selection - only proceed if clear was successful
    const clearSuccessful = handleClearSelection();
    if (clearSuccessful) {
      proceed();
      return true; // tells Prompt to allow navigation
    }
    return false; // tells Prompt to block navigation
  }, [notificationActions, handleClearSelection, undoChanges, handleSave, isLoading, active]);

  // Add handleAnnotationClick for form annotations
  const handleAnnotationClick = useCallback((selectedType, clickedAnnotation, clickDetails) => {
    // Deselect if stage is clicked
    if (selectedType === 'stage') {
      // If in edit mode and selectableType is set, this is where creation happens
      if (editMode && selectableType && clickDetails) {
        handleTransition(() => {
          createFormAnnotation(clickDetails, selectableType, annotationHandler, parent, {
            defaultColor
          });
        });
        return;
      }
      handleTransition(() => {
        // Only call clearSelection if handleTransition allows it
        const clearSuccessful = handleClearSelection();
        if (!clearSuccessful) {
          // If clear was blocked, don't proceed with the transition
        }
      });
      return;
    }

    // Safely handle null clickedAnnotation
    const annotationKey = getAnnotationIdentifier(selectedType, clickedAnnotation && clickedAnnotation.formType);
    if (clickedAnnotation && annotationKey === selectableType) {
      handleTransition(() => {
        select(clickedAnnotation);
      });
      return;
    }

    // If a different type is clicked (but not stage), clear selection
    // Always use handleTransition to check for pending changes, regardless of active annotation
    if (selectedType && clickedAnnotation) {
      handleTransition(() => {
        // Only call clearSelection if handleTransition allows it
        const clearSuccessful = handleClearSelection();
        if (!clearSuccessful) {
          // If clear was blocked, don't proceed with the transition
        }
      });
    }
  }, [annotationHandler, createFormAnnotation, defaultColor, editMode, handleTransition, handleClearSelection, select, selectableType, parent]);

  // Handle PDF download
  const handleDownload = () => {
    var _parent$content, _toolbar$annotationTo;
    const formId = parent === null || parent === void 0 || (_parent$content = parent.content) === null || _parent$content === void 0 ? void 0 : _parent$content.id;
    if (!formId) {
      notificationActions.error({
        message: 'Form ID is missing. Cannot download PDF without a valid Form ID.',
        autoClose: 5000
      });
      return;
    }
    // Extract formTypeIdentifiers from toolbar array (same as loadAnnotations)
    const formTypeIdentifiers = toolbar === null || toolbar === void 0 || (_toolbar$annotationTo = toolbar.annotationTools) === null || _toolbar$annotationTo === void 0 ? void 0 : _toolbar$annotationTo.map(item => item.formTypeIdentifier);
    // Send all the data needed to recreate queryFormBehavior
    serverRenderAnnotatedForm(formId, annotationDownloadPDFPath, formTypeIdentifiers, filters);
  };

  // Callback to evaluate annotationHandler.create and return the color
  // This is passed to PDF component so it can get the correct color before drawing starts
  // Uses the local helper function defined above
  const handleEvaluateAnnotationHandlerColor = useCallback(async (baseType, formTypeIdentifier) => evaluateAnnotationHandlerColor(baseType, formTypeIdentifier, annotationHandler, parent, defaultColor), [annotationHandler, parent, defaultColor]);

  // Initialize component state on mount and cleanup on unmount
  useEffect(() => {
    // Set annotation context and clear any existing selections
    setAnnotationContext('form');
    clearSelection();

    // Reset toolbar selection and color to defaults
    setSelectableType(null);
    setDefaultColor(DEFAULT_COLOR);

    // Cleanup function to clear selections when component unmounts
    return () => {
      clearSelection();
    };
  }, [setAnnotationContext, clearSelection, setSelectableType, setDefaultColor]);
  if (!cachePath) {
    return /*#__PURE__*/React.createElement(Alert, {
      color: "warning",
      className: "m-3"
    }, "No document selected for editing. Please select a document to edit from the form view.");
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Prompt, {
    when: pendingChanges,
    message: location => handleTransition(() => hashHistory.replace(location.pathname))
  }), /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    style: {
      width: '100%',
      height: '100%',
      position: 'relative',
      display: 'flex',
      padding: '0 1rem',
      boxSizing: 'border-box'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: `${leftWidth}%`,
      height: '100%',
      overflow: 'hidden',
      padding: '0 0 0 0',
      boxSizing: 'border-box'
    },
    "data-testid": "t-fae-left-column"
  }, /*#__PURE__*/React.createElement(PDF, {
    enableEditAnnotations: !renderReadonly,
    asyncDownloadPath: true,
    button: null,
    containerMaxHeight: maxHeight,
    onAnnotationSelect: handleAnnotationClick,
    handleTransition: handleTransition,
    download: handleDownload,
    clearDownload: clearDownloadPDFPath,
    downloadPDFPath: downloadPDFPath,
    clearSelection: clearSelection,
    title: title,
    src: cachePath,
    evaluateAnnotationHandlerColor: handleEvaluateAnnotationHandlerColor
  })), /*#__PURE__*/React.createElement("div", {
    role: "slider",
    "aria-label": "Column width",
    "aria-orientation": "horizontal",
    "aria-valuenow": leftWidth,
    "aria-valuemin": 20,
    "aria-valuemax": 80,
    tabIndex: 0,
    onKeyDown: e => {
      if (e.key === 'ArrowLeft') {
        setLeftWidth(prev => Math.max(prev - 2, 20));
      } else if (e.key === 'ArrowRight') {
        setLeftWidth(prev => Math.min(prev + 2, 80));
      }
    },
    style: {
      width: '2rem',
      cursor: 'col-resize',
      height: '100%',
      position: 'absolute',
      left: `calc(${leftWidth}% - 1rem)`,
      top: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    onMouseDown: handleMouseDown
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: `${100 - leftWidth}%`,
      height: '100%',
      overflow: 'hidden',
      padding: '0 0 0 20px',
      boxSizing: 'border-box'
    },
    "data-testid": "t-fae-right-column"
  }, /*#__PURE__*/React.createElement(FormAnnotationEditorSidebar, {
    containerMaxHeight: maxHeight,
    displayType: selectableType ? selectableType.split('__')[0] : null
  }))));
};
FormAnnotationEditor.propTypes = {
  annotationSelection: PropTypes.shape({
    defaultColor: PropTypes.string,
    editMode: PropTypes.bool,
    selectableType: PropTypes.string
  }).isRequired,
  annotation: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string
    }),
    editorState: PropTypes.shape({
      annotationHandler: PropTypes.shape({}),
      parent: PropTypes.shape({
        content: PropTypes.shape({
          id: PropTypes.string
        }),
        contentPath: PropTypes.string
      }),
      remotePath: PropTypes.shape({
        _cache: PropTypes.string,
        _download: PropTypes.string
      }).isRequired,
      title: PropTypes.string,
      renderReadonly: PropTypes.bool,
      filters: PropTypes.shape({}),
      toolbar: PropTypes.shape({
        annotationTools: PropTypes.arrayOf(PropTypes.shape({
          formTypeIdentifier: PropTypes.string
        }))
      }),
      formID: PropTypes.string
    }).isRequired,
    isLoading: PropTypes.bool
  }).isRequired,
  form: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string
    }),
    pendingChanges: PropTypes.bool,
    loading: PropTypes.bool,
    downloadPDFPath: PropTypes.string
  }).isRequired,
  annotationActions: PropTypes.shape({
    loadPersistedState: PropTypes.func.isRequired,
    loadAnnotations: PropTypes.func.isRequired
  }).isRequired,
  annotationSelectionActions: PropTypes.shape({
    clearSelection: PropTypes.func.isRequired,
    select: PropTypes.func.isRequired,
    setAnnotationContext: PropTypes.func.isRequired,
    setDefaultColor: PropTypes.func.isRequired,
    setSelectableType: PropTypes.func.isRequired
  }).isRequired,
  formActions: PropTypes.shape({
    serverSave: PropTypes.func.isRequired,
    undoChanges: PropTypes.func.isRequired,
    createFormAnnotation: PropTypes.func.isRequired,
    serverRenderAnnotatedForm: PropTypes.func.isRequired,
    clearDownloadPDFPath: PropTypes.func.isRequired
  }).isRequired,
  notificationActions: PropTypes.shape({
    info: PropTypes.func.isRequired,
    dismiss: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired
  }).isRequired
};
export { FormAnnotationEditor as UnComposedFormAnnotationEditor };
export default entityComposition(FormAnnotationEditor, ['annotationSelection', 'annotation', 'form'], {}, ['notification', 'form']);